import React from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import designTokens from "../../styles/designTokens";

type SupportRequestConfirmStatus = "idle" | "sending" | "success" | "error";

interface SupportRequestConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  onSendRequest: () => void;
  onRetry: () => void;
  status?: SupportRequestConfirmStatus;
}

const SupportRequestConfirmModal: React.FC<SupportRequestConfirmModalProps> = ({
  open,
  onClose,
  onCancel,
  onSendRequest,
  onRetry,
  status = "idle",
}) => {
  const isSuccess = status === "success";
  const isError = status === "error";
  const isSending = status === "sending";

  const title = isSuccess ? "Request sent" : "Request support";
  const description = isSuccess
    ? "We received your request and will suggest a solution tailored to your situation."
    : isError
    ? "We couldn't prepare your support request. Please try again."
    : "Our team can help you upload and view your files. Send us a request, and we'll suggest a solution tailored to your situation.";

  const primaryLabel = isSuccess
    ? "Close"
    : isError
    ? "Retry"
    : isSending
    ? "Sending..."
    : "Send request";

  const handlePrimaryAction = () => {
    if (isSuccess) {
      onClose();
      return;
    }

    if (isError) {
      onRetry();
      return;
    }

    onSendRequest();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="body"
      maxWidth={false}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.5)" } },
      }}
      PaperProps={{
        sx: {
          width: 500,
          minWidth: 500,
          backgroundColor: designTokens.fill.background.grey20.dark,
          color: designTokens.fill.text.common100.dark,
          borderRadius: designTokens.numbers.cornerRadius[16],
          overflow: "hidden",
          boxShadow: designTokens.shadows.modal,
        },
      }}
    >
      <Box sx={{ height: 16, width: "100%" }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 64,
          px: designTokens.numbers.gap[32],
          gap: designTokens.numbers.gap[8],
        }}
      >
        <Typography
          sx={{
            ...designTokens.typography.styles.heading,
            color: designTokens.fill.text.grey90.dark,
            flex: 1,
          }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: designTokens.fill.icon.grey80.dark, p: 0, width: 24, height: 24 }}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: designTokens.numbers.gap[32], pb: designTokens.numbers.gap[24] }}>
        <Typography
          sx={{
            ...designTokens.typography.styles.headline1,
            color: designTokens.fill.text.grey90.dark,
            mb: designTokens.numbers.gap[24],
          }}
        >
          {description}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: designTokens.numbers.gap[24],
            height: 64,
            alignItems: "center",
          }}
        >
          {!isSuccess ? (
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isSending}
              sx={{
                height: 48,
                minWidth: 100,
                px: designTokens.numbers.gap[12],
                textTransform: "none",
                borderColor: designTokens.stroke.grey60.dark,
                color: designTokens.fill.text.grey80.dark,
                borderRadius: designTokens.numbers.cornerRadius[8],
              }}
            >
              Cancel
            </Button>
          ) : null}

          <Button
            variant="contained"
            onClick={handlePrimaryAction}
            disabled={isSending}
            sx={{
              height: 48,
              minWidth: 100,
              px: designTokens.numbers.gap[12],
              textTransform: "none",
              backgroundColor: designTokens.fill.shape.primary50.dark,
              color: designTokens.fill.text.common100.dark,
              borderRadius: designTokens.numbers.cornerRadius[8],
              "&:hover": { backgroundColor: designTokens.fill.shape.primary50.dark },
              "&.Mui-disabled": {
                backgroundColor: designTokens.fill.shape.primary50.dark,
                color: designTokens.fill.text.common100.dark,
                opacity: 0.8,
              },
            }}
          >
            {isSending ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: designTokens.numbers.gap[8] }}>
                <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} />
                <span>{primaryLabel}</span>
              </Box>
            ) : (
              primaryLabel
            )}
          </Button>
        </Box>
      </Box>
      <Box sx={{ height: 16, width: "100%" }} />
    </Dialog>
  );
};

export default SupportRequestConfirmModal;
